import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Empty, Space } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { FaFilter } from 'react-icons/fa';
import { SOSMultiSelectCheckboxDropDown } from '@/components/inputs/multi-select-checkbox-dropdown';
import { MultiSelectDropdownEnhancedWrapper } from '@/components/inputs/styles/multi-select-dropdown-enhanced.styles';
import {
  setFilter,
  clearAllFilters,
} from '@/pages/reports/regression-dashboard/regressionDashboardSlice';
import {
  FilterContainer,
  FilterHeader,
  FilterTitle,
  FilterBody,
  FilterItem,
  FilterLabel,
  FilterActions,
  ReleaseDashboardFilterContainer,
} from './styles/CommonFilter.styles';

/**
 * Normalize a filter option into `{ label, value }` for the multi-select.
 * Accepts the new `{ key, name }` API shape as well as legacy string arrays.
 */
const toDropdownOption = (opt) => {
  if (opt == null) return null;
  if (typeof opt === 'string' || typeof opt === 'number') {
    return { label: String(opt), value: opt };
  }
  const hasKey = Object.prototype.hasOwnProperty.call(opt, 'key');
  const value = hasKey ? opt.key : (opt.value ?? opt.id);
  const label = opt.name ?? opt.label ?? String(value ?? '');
  if (value === undefined || (value === null && !hasKey)) return null;
  return { label, value };
};

/**
 * Common dashboard filter component
 *
 * Renders the provided filter definitions, using each definition's key to
 * read its options from `filterOptions`.
 *
 * @param {Object}   props
 * @param {Function} props.hideFilter    - Close drawer callback.
 * @param {Object}   props.filterOptions - Response from filter options API.
 * @param {Array}    props.filterDefinitions - Optional static filter list.
 */
export const CommonFilter = ({
  hideFilter,
  filterOptions = {},
  filterDefinitions,
  filtersOverride,
  onFilterChange,
  onClearAll,
  onApply,
  customFilterRenderers,
  applyDisabled = false,
  appearance = 'default',
}) => {
  const dispatch = useDispatch();
  const regressionFilters = useSelector(
    (state) => state.regressionDashboard.filters,
  );
  const filters = filtersOverride || regressionFilters;
  const isReleaseAppearance = appearance === 'release';
  const Container = isReleaseAppearance
    ? ReleaseDashboardFilterContainer
    : FilterContainer;
  const Header = isReleaseAppearance ? 'div' : FilterHeader;
  const Body = isReleaseAppearance ? 'div' : FilterBody;
  const Item = isReleaseAppearance ? 'div' : FilterItem;
  const Label = isReleaseAppearance ? 'span' : FilterLabel;
  const Actions = isReleaseAppearance ? 'div' : FilterActions;
  const ControlWrapper = isReleaseAppearance
    ? React.Fragment
    : MultiSelectDropdownEnhancedWrapper;

  const filtersToRender =
    filterDefinitions ??
    (Array.isArray(filterOptions.availableFilters)
      ? filterOptions.availableFilters
      : []);

  const handleFilterChange = (filterKey, value) => {
    if (onFilterChange) {
      onFilterChange(filterKey, value);
      return;
    }
    dispatch(setFilter({ filterKey, value }));
  };

  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
      return;
    }
    dispatch(clearAllFilters());
  };

  return (
    <Container>
      <Header className={isReleaseAppearance ? 'filter-header' : undefined}>
        {isReleaseAppearance ? (
          <h3>
            <span className="header-text">
              <FaFilter className="filter-icon" />
              Release QA Filters
            </span>
          </h3>
        ) : (
          <FilterTitle>Filters</FilterTitle>
        )}
        <Button
          type="text"
          icon={<CloseOutlined />}
          aria-label="Close filters"
          onClick={hideFilter}
        />
      </Header>

      <Body className={isReleaseAppearance ? 'filters-content' : undefined}>
        {filtersToRender.length === 0 ? (
          <Empty
            description="No filters available for the selected project(s)"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Space
            direction="vertical"
            size={isReleaseAppearance ? 0 : 'large'}
            style={{ width: '100%' }}
          >
            {filtersToRender.map(({ key, name }) => {
              const rawOptions = Array.isArray(filterOptions[key])
                ? filterOptions[key]
                : [];
              const options = rawOptions.map(toDropdownOption).filter(Boolean);
              const customRenderer = customFilterRenderers?.[key];
              const control = customRenderer ? (
                customRenderer({
                  filterKey: key,
                  filterName: name,
                  value: filters[key] || [],
                  onChange: (value) => handleFilterChange(key, value),
                  options,
                  filterOptions,
                })
              ) : (
                <ControlWrapper>
                  <SOSMultiSelectCheckboxDropDown
                    placeholder={`Select ${name}`}
                    defaultValue={filters[key] || []}
                    handleChange={(value) => handleFilterChange(key, value)}
                    options={options}
                    maxLabelsToShow={1}
                    enableSearch={true}
                    enableSelectAll={true}
                  />
                </ControlWrapper>
              );

              return (
                <Item
                  key={key}
                  className={isReleaseAppearance ? 'filter-item' : undefined}
                >
                  <Label className={isReleaseAppearance ? 'title' : undefined}>
                    {name}
                  </Label>
                  {control}
                </Item>
              );
            })}
          </Space>
        )}
      </Body>

      <Actions className={isReleaseAppearance ? 'filter-actions' : undefined}>
        {!isReleaseAppearance && (
          <Button
            className={isReleaseAppearance ? 'apply-filters' : undefined}
            type="primary"
            disabled={applyDisabled}
            onClick={() => {
              if (applyDisabled) return;
              if (onApply) onApply();
              hideFilter?.();
            }}
            style={{
              background: 'linear-gradient(135deg, #6846d1 0%, #8b5cf6 100%)',
              border: 'none',
              width: '100%',
            }}
          >
            Apply Filters
          </Button>
        )}
        <Button
          type={isReleaseAppearance ? 'primary' : undefined}
          size={isReleaseAppearance ? 'large' : undefined}
          danger={isReleaseAppearance}
          onClick={handleClearAll}
          style={{ width: '100%' }}
        >
          Clear All
        </Button>
      </Actions>
    </Container>
  );
};

CommonFilter.propTypes = {
  hideFilter: PropTypes.func,
  filterOptions: PropTypes.object,
  filterDefinitions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  filtersOverride: PropTypes.object,
  onFilterChange: PropTypes.func,
  onClearAll: PropTypes.func,
  onApply: PropTypes.func,
  customFilterRenderers: PropTypes.object,
  applyDisabled: PropTypes.bool,
  appearance: PropTypes.oneOf(['default', 'release']),
};
